import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Spinner,
  Status,
  toast,
} from '@elbklar/ui';
import { LostReasonModal } from './LostReasonModal';
import { FollowUpModal } from './FollowUpModal';
import { statusAuswahl, statusFehlerText, statusPhase } from './statusWechsel';
import { customersApi } from '@/lib/api';
import type { Customer, CustomerStatus } from '@/types';

type StatusPayload = {
  status: CustomerStatus;
  lost_reason?: string | null;
  follow_up_at?: string | null;
};

const isLostStatus = (s: CustomerStatus) => s === 'lost' || s === 'lead_lost';

interface StatusDropdownProps {
  customer: Customer;
  /**
   * Der Weg in die naechste Phase, wenn die Liste einen anbietet. In der
   * Leadliste fuehrt er in den Umwandeln-Dialog (K-Nummer, Aktivierungsdatum),
   * nicht in einen Statuswechsel.
   */
  weiter?: { label: string; onSelect: () => void };
}

/**
 * Statuswechsel aus einer Liste.
 *
 * Welche Status das Menue anbietet, entscheidet die Phase des Datensatzes
 * (`statusPhase`), nicht die Liste, die ihn zeigt: ein Lead wechselt zwischen
 * den Lead-Status, ein Kunde zwischen den Kundenstatus. Zum Kunden wird ein Lead
 * nur ueber `weiter` — `POST /leads/{id}/convert` zieht dort die K-Nummer und
 * setzt `activated_at`, ein schlichter Statuswechsel tut beides nicht.
 *
 * „Verloren" verlangt einen Grund, „Spaeter" eine Wiedervorlage
 * (CustomerUpdate-Validator im Backend); bis zum 22.10.2026 schickte die Liste
 * nur den Status, bekam 422 und zeigte nichts — der Status blieb stehen, ohne
 * Meldung (Audit CRM-1). Dieselben Dialoge wie auf der Detailseite
 * (StatusSelect).
 *
 * Scheitert der Wechsel, sagt das Menue es: am 29.10.2026 schlugen vier Wechsel
 * in drei Sekunden fehl (401, Anmeldung abgelaufen), und zu sehen war davon
 * nichts. Solange geschrieben wird, laeuft ein Spinner — ohne ihn sieht ein
 * langsamer Wechsel aus wie ein ignorierter Klick.
 */
export function StatusDropdown({ customer, weiter }: StatusDropdownProps) {
  const queryClient = useQueryClient();
  const [pendingStatus, setPendingStatus] = useState<CustomerStatus | null>(null);
  const [showLost, setShowLost] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  const auswahl = statusAuswahl(statusPhase(customer.status));

  const schliessen = () => {
    setPendingStatus(null);
    setShowLost(false);
    setShowFollowUp(false);
  };

  const mutation = useMutation({
    mutationFn: (payload: StatusPayload) => customersApi.update(customer.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      schliessen();
    },
    onError: (err) => {
      toast.error(statusFehlerText(err));
    },
  });

  const waehlen = (value: CustomerStatus) => {
    if (value === customer.status) return;
    if (isLostStatus(value)) {
      setPendingStatus(value);
      setShowLost(true);
      return;
    }
    if (value === 'interested_later') {
      setPendingStatus(value);
      setShowFollowUp(true);
      return;
    }
    mutation.mutate({ status: value });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            aria-label="Status ändern"
            disabled={mutation.isPending}
            aria-busy={mutation.isPending || undefined}
            className="inline-flex cursor-pointer items-center gap-2 rounded-full disabled:cursor-default disabled:opacity-45"
          >
            <Status status={customer.status} />
            {mutation.isPending && <Spinner size={14} label="Status wird geändert" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
          {auswahl.map((value) => (
            <DropdownMenuItem key={value} onSelect={() => waehlen(value)}>
              <span className="flex items-center justify-between gap-3">
                <Status status={value} variant="plain" />
                {value === customer.status && <Check size={16} strokeWidth={1.75} className="text-highlight" aria-hidden />}
              </span>
            </DropdownMenuItem>
          ))}
          {weiter && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={ShieldCheck} onSelect={weiter.onSelect}>
                {weiter.label}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Die Dialoge liegen ausserhalb der Tabellenzeile; ein Klick darin darf
          nicht die Zeile oeffnen. */}
      <div onClick={(e) => e.stopPropagation()}>
        <LostReasonModal
          open={showLost}
          onCancel={schliessen}
          onSubmit={(reason) => pendingStatus && mutation.mutate({ status: pendingStatus, lost_reason: reason })}
        />
        <FollowUpModal
          open={showFollowUp}
          initialValue={customer.follow_up_at}
          onCancel={schliessen}
          onSubmit={(iso) => pendingStatus && mutation.mutate({ status: pendingStatus, follow_up_at: iso })}
        />
      </div>
    </>
  );
}
